import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AutoAwesomeMotionOutlinedIcon from '@mui/icons-material/AutoAwesomeMotionOutlined'
import LogoutIcon from '@mui/icons-material/Logout'
import LoyaltyIcon from '@mui/icons-material/Loyalty'
import PaymentIcon from '@mui/icons-material/Payment'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import RedeemIcon from '@mui/icons-material/Redeem'
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import { Fragment, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import AddIcon from '@mui/icons-material/Add'
import { useBookingContext } from '@providers/booking'
import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { i18WithParams as t } from '@shared/locale'
import { getHasSharedCRM } from '@shared/locations'
import type { TRouteNames } from 'app/types'
import { useRoutes } from 'app/use_routes'
import { PetAddOnIcon } from 'components-ui/src/asset/pet_addon_icon'
import { PetMenuIcon } from 'components-ui/src/asset/pet_menu_icon'
import { WaitlistIcon } from 'components-ui/src/asset/waitlist_icon'
import { useClientServiceAgreementContract } from 'service-api/src/hooks/use_client_contracts'
import { useClientLocationCamerasAccess } from 'service-api/src/hooks/use_client_location_cameras'
import { useClientLocationServiceType } from 'service-api/src/hooks/use_client_location_service_type'
import { useLocationUserPetReportCards } from './hooks/use_location_user_pet_report_cards'

interface TMenuItem {
  handle: string
  label: string
  icon: React.ElementType
  routePath: string
  route: TRouteNames
  enabled: boolean
}

const menuItems = [
  {
    handle: 'profile',
    label: t('profileNav.profile'),
    icon: AccountCircleIcon,
    routePath: 'profile/my-profile',
    route: 'myprofile',
    enabled: true,
  },
  {
    handle: 'pets',
    label: t('profileNav.pets'),
    icon: PetAddOnIcon,
    routePath: 'profile/pets',
    route: 'profilePet',
    enabled: true,
  },
  {
    handle: 'cameras',
    label: t('profileNav.cameras'),
    icon: PhotoCameraIcon,
    routePath: 'profile/cameras',
    route: 'profileCameras',
    enabled: true,
  },
  {
    handle: 'bookings',
    label: t('profileNav.bookings'),
    icon: PetMenuIcon,
    routePath: 'profile/bookings',
    route: 'profileBookings',
    enabled: true,
  },
  {
    handle: 'waitlist',
    label: t('profileNav.waitlist'),
    icon: WaitlistIcon,
    routePath: 'profile/waitlist',
    route: 'profileWaitlist',
    enabled: true,
  },
  {
    handle: 'report-cards',
    label: t('profileNav.reportCards'),
    icon: AutoAwesomeMotionOutlinedIcon,
    routePath: 'profile/report-cards',
    route: 'profileReportCards',
    enabled: true,
  },
  {
    handle: 'payment-methods',
    label: t('profileNav.paymentMethods'),
    icon: PaymentIcon,
    routePath: 'profile/payments',
    route: 'profilePayments',
    enabled: true,
  },
  {
    handle: 'memberships',
    label: t('profileNav.memberships'),
    icon: LoyaltyIcon,
    routePath: 'profile/memberships',
    route: 'profileMemberships',
    enabled: true,
  },
  {
    handle: 'packages',
    label: t('profileNav.packages'),
    icon: RedeemIcon,
    routePath: 'profile/packages',
    route: 'profilePackages',
    enabled: true,
  },
  {
    handle: 'agreements',
    label: t('profileNav.agreements'),
    icon: StickyNote2OutlinedIcon,
    routePath: 'profile/agreements',
    route: 'profileAgreements',
    enabled: true,
  },
  {
    handle: 'additional-contacts',
    label: t('profileNav.additionalContacts'),
    icon: PersonAddIcon,
    routePath: 'profile/contacts',
    route: 'profileContacts',
    enabled: true,
  },
  {
    handle: 'logout',
    label: t('profileNav.logout'),
    icon: LogoutIcon,
    routePath: 'profile/logout',
    route: 'logout',
    enabled: true,
  },
] as TMenuItem[]

interface TProfileNavProps {
  onClick?: (menuItem: TMenuItem) => void
}

export const ProfileNav = ({ onClick }: TProfileNavProps) => {
  const { accountName: locationName = '' } = useParams()
  const sessionCtx = useSessionContext()
  const bookingCtx = useBookingContext()
  const { navigateRoute } = useRoutes()
  const { location } = useLocationContext()
  const hasSharedCRM = getHasSharedCRM(location)
  const serviceTypes = useClientLocationServiceType(locationName)
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)
  const contracts = useClientServiceAgreementContract(locationName, {
    hasSharedCRM,
    enabled: !!sessionCtx.state?.user?.lupId,
  })
  const cameras = useClientLocationCamerasAccess(locationName, sessionCtx.state?.user?.lupId || '')

  const hasPackages = useMemo(() => {
    if (serviceTypes.isSuccess) {
      return serviceTypes.data.results.some((serviceType) => serviceType.serviceType?.name === 'packages')
    } else {
      return false
    }
  }, [serviceTypes.data, serviceTypes.isSuccess])

  const hasMemberships = useMemo(() => {
    if (serviceTypes.isSuccess) {
      return serviceTypes.data.results.some((serviceType) => serviceType.serviceType?.name === 'memberships')
    } else {
      return false
    }
  }, [serviceTypes.data, serviceTypes.isSuccess])

  const hasAccessToCameras = useMemo(() => {
    if (cameras?.isSuccess) {
      return cameras?.data?.hasConfig
    } else {
      return false
    }
  }, [cameras?.data, cameras?.isSuccess])

  const hasAccessToReportCards = useMemo(() => {
    if (location?.subscriberGroup) {
      return location?.subscriberGroup.reportCardEnabled
    } else {
      return false
    }
  }, [location?.subscriberGroup])
  const reportCards: boolean = useLocationUserPetReportCards({
    lupId: sessionCtx.state?.user?.lupId || '',
    enabled: !!hasAccessToReportCards,
  })

  const hasReportCardsToRender = useMemo(() => {
    if (!hasAccessToReportCards) {
      // if they dont have access, there's no point in checking further
      return false
    } else {
      return reportCards
    }
  }, [hasAccessToReportCards, reportCards])

  const hasContracts = useMemo(
    () => contracts.isSuccess && contracts?.data && contracts?.data?.results?.length > 0,
    [contracts.data, contracts.isSuccess]
  )

  const hasWaitlist = useMemo(() => {
    if (location?.subscriberGroup) {
      return location.subscriberGroup.waitlistEnabled
    } else {
      return false
    }
  }, [location?.subscriberGroup])

  const handleMenuClick = (menuItem: TMenuItem) => {
    if (menuItem.handle === 'logout') {
      setShowLogoutDialog(true)
      return
    }

    onClick?.(menuItem)
    setTimeout(() => navigateRoute(menuItem.route, { accountName: locationName }), 0)
  }

  const handleLogout = () => {
    setShowLogoutDialog(false)
    bookingCtx.dispatch({ type: 'RESET_STATE_FULL' })
    sessionCtx.dispatch({ type: 'SIGNOUT' })
    return
  }

  const navigateToHome = useCallback(() => {
    navigateRoute('locationHome', {})
  }, [navigateRoute])

  return (
    <Fragment>
      <Button
        variant="outlined"
        color="primary"
        size="medium"
        onClick={navigateToHome}
        startIcon={<AddIcon />}
        fullWidth
        sx={{
          marginBottom: '20px',
        }}
      >
        {t('global.label.newBooking')}
      </Button>
      {menuItems
        .filter(
          (item) =>
            item.enabled &&
            !(
              (item.handle === 'packages' && !hasPackages) ||
              (item.handle === 'agreements' && !hasContracts) ||
              (item.handle === 'memberships' && !hasMemberships) ||
              (item.handle === 'cameras' && !hasAccessToCameras) ||
              (item.handle === 'report-cards' && !hasReportCardsToRender) ||
              (item.handle === 'waitlist' && !hasWaitlist)
            )
        )
        .map((item) => (
          <MenuItem
            key={item.handle}
            id={item.handle}
            selected={window.location.pathname.includes(item.routePath)}
            onClick={() => handleMenuClick(item)}
          >
            <ListItemIcon>
              <item.icon />
            </ListItemIcon>
            <ListItemText>{item.label}</ListItemText>
          </MenuItem>
        ))}
      <Dialog open={showLogoutDialog} onClose={() => setShowLogoutDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('profile.navBlocker.title')}</DialogTitle>
        <DialogContent>{t('profile.logOut.dialogContent')}</DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', padding: '8px 16px 16px 16px' }}>
          <Button onClick={handleLogout} color="secondary" variant="contained">
            {t('loginMenu.logOut')}
          </Button>
          <Button onClick={() => setShowLogoutDialog(false)} color="primary">
            {t('profile.navBlocker.back')}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  )
}

export const ProfileNavMenu = () => {
  return (
    <MenuList>
      <ProfileNav />
    </MenuList>
  )
}
